import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { businessInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });
      
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-page via-card to-page">
        <div className="container mx-auto px-6 text-center">
          <h1 className="heading-1 text-6xl md:text-7xl lg:text-8xl font-black text-primary uppercase mb-6 leading-tight">
            Contact Us
          </h1>
          <p className="body-large text-secondary max-w-3xl mx-auto">
            Questions? Ready to book? We&apos;re here to help make your experience legendary.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Contact Information */}
            <div>
              <h2 className="heading-3 text-4xl font-black text-primary uppercase mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-inverse" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-5 text-primary font-bold mb-1 uppercase">Location</h3>
                    <p className="text-secondary text-sm leading-relaxed">{businessInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-inverse" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-5 text-primary font-bold mb-1 uppercase">Phone</h3>
                    <a href={`tel:${businessInfo.phone}`} className="text-secondary hover:text-primary transition-colors text-sm">
                      {businessInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-inverse" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-5 text-primary font-bold mb-1 uppercase">Email</h3>
                    <div className="space-y-1">
                      {businessInfo.emails.map((email, idx) => (
                        <a key={idx} href={`mailto:${email}`} className="text-secondary hover:text-primary transition-colors text-sm block break-all">
                          {email}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-inverse" size={24} />
                  </div>
                  <div>
                    <h3 className="heading-5 text-primary font-bold mb-2 uppercase">Hours</h3>
                    <div className="space-y-1 text-sm">
                      <p className="text-secondary">Monday - Thursday: {businessInfo.hours.monday}</p>
                      <p className="text-secondary">Friday - Saturday: {businessInfo.hours.friday}</p>
                      <p className="text-secondary">Sunday: {businessInfo.hours.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-card border border-medium rounded-2xl p-8">
              <h2 className="heading-4 text-primary font-bold mb-6 uppercase">Send Us A Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="caption text-primary mb-2 block">Name *</label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-page border-medium text-primary"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="caption text-primary mb-2 block">Email *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-page border-medium text-primary"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="caption text-primary mb-2 block">Phone</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-page border-medium text-primary"
                    placeholder="(123) 456-7890"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="caption text-primary mb-2 block">Message *</label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full bg-page border-medium text-primary resize-none"
                    placeholder="Tell us what you're looking for..."
                  />
                </div>

                <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>

          {/* Map */}
          <div className="bg-card border border-medium rounded-2xl overflow-hidden">
            <div className="h-96 bg-olive flex items-center justify-center">
              <div className="text-center">
                <MapPin className="text-primary mx-auto mb-4" size={48} />
                <p className="text-primary font-bold text-lg">Map Placeholder</p>
                <p className="text-secondary text-sm mt-2">{businessInfo.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};